import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import LaserEtchedLabel from "./LaserEtchedLabel";

const links = [
  { to: "/", label: "Home" },
  { to: "/gallery", label: "Our Work" },
  { to: "/what-we-make", label: "What We Make" },
  { to: "/events", label: "Events" },
  { to: "/process", label: "How It Works" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];
function EngravedNavItem({ children, label, className = "", ...props }) {
  return (
    <span className={`plank-nav-item ${className}`} {...props}>
      <LaserEtchedLabel text={label} className="plank-nav-label">{children}</LaserEtchedLabel>
    </span>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { itemCount } = useCart();

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return undefined;
    window.resetLaserEntrance = () => {
      window.location.reload();
    };
    return () => { delete window.resetLaserEntrance; };
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className="site-plank-header fixed inset-x-0 top-0 z-40 h-20"
    >
      <div className="site-plank-inner max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="site-plank-brand flex items-center gap-3 group" data-testid="navbar-brand">
          <img
            src="/branding/bbw-stamp.png"
            alt="Beyond Board Woodworking logo"
            className="h-14 w-auto object-contain drop-shadow-[0_0_18px_rgba(38,198,213,0.35)]"
          />
        </Link>

        <nav className="site-desktop-nav items-center gap-1">
          {links.map((l, index) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) => `plank-nav-link ${isActive ? "plank-nav-active" : ""}`}
            >
              <EngravedNavItem label={l.label}>{l.label}</EngravedNavItem>
            </NavLink>
          ))}
          <Link to="/quote" className="plank-nav-link ml-2">
            <Button
              data-testid="nav-cta-quote"
              className="plank-nav-button"
            >
              <EngravedNavItem label="Request a Custom Quote">Request a Custom Quote</EngravedNavItem>
            </Button>
          </Link>
          <Link to="/cart" className="plank-nav-link ml-1" aria-label={`Cart, ${itemCount} items`}>
            <Button variant="outline" className="plank-nav-button">
              <EngravedNavItem>Cart {itemCount > 0 && <span className="plank-nav-count">{itemCount}</span>}</EngravedNavItem>
            </Button>
          </Link>
          {!user ? (
            <Link to="/admin/login" className="plank-nav-link ml-1">
              <Button
                variant="outline"
                data-testid="nav-admin-login"
                className="plank-nav-button"
              >
                <EngravedNavItem label="Admin Login">Admin Login</EngravedNavItem>
              </Button>
            </Link>
          ) : (
            <div className="ml-1 flex items-center gap-1">
              <Link to="/admin" className="plank-nav-link">
                <Button
                  variant="outline"
                  data-testid="nav-admin-dashboard"
                  className="plank-nav-button"
                >
                  <EngravedNavItem label="Admin Dashboard">Admin Dashboard</EngravedNavItem>
                </Button>
              </Link>
              <Link to="/admin/inventory" className="plank-nav-link">
                <Button
                  variant="outline"
                  data-testid="nav-admin-inventory"
                  className="plank-nav-button"
                >
                  <EngravedNavItem>Inventory</EngravedNavItem>
                </Button>
              </Link>
              <Button
                variant="ghost"
                data-testid="nav-admin-logout"
                onClick={handleLogout}
                className="plank-nav-button"
              >
                <EngravedNavItem>Log Out</EngravedNavItem>
              </Button>
            </div>
          )}
        </nav>

        <button
          className="site-nav-toggle text-[#F5F0E6] p-2"
          onClick={() => setOpen(!open)}
          data-testid="navbar-toggle"
          aria-label="toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="site-plank-mobile absolute inset-x-0 top-full border-t border-[#6b3718]/60">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`px-3 py-2 rounded-md text-sm ${
                  location.pathname === l.to
                    ? "text-[#26C6D5]"
                    : "text-[#B8B7B2] hover:text-[#F5F0E6]"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/quote" onClick={() => setOpen(false)} className="mt-2">
              <Button className="w-full bg-[#D8A34B] hover:bg-[#c6903b] text-[#15191C]">
                Request a Custom Quote
              </Button>
            </Link>
            <Link to="/cart" onClick={() => setOpen(false)} className="mt-2">
              <Button variant="outline" className="w-full border-[#26C6D5]/30 text-[#26C6D5]">Cart {itemCount > 0 && `(${itemCount})`}</Button>
            </Link>
            <div className="mt-3 pt-3 border-t border-[#F5F0E6]/10">
              {!user ? (
                <Link to="/admin/login" onClick={() => setOpen(false)}>
                  <Button
                    variant="outline"
                    data-testid="mobile-nav-admin-login"
                    className="w-full h-10 text-xs bg-transparent border-[#F5F0E6]/15 text-[#B8B7B2]"
                  >
                    Admin Login
                  </Button>
                </Link>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link to="/admin" onClick={() => setOpen(false)}>
                    <Button
                      variant="outline"
                      data-testid="mobile-nav-admin-dashboard"
                      className="w-full h-10 text-xs bg-transparent border-[#D8A34B]/45 text-[#D8A34B]"
                    >
                      Admin Dashboard
                    </Button>
                  </Link>
                  <Link to="/admin/inventory" onClick={() => setOpen(false)}>
                    <Button
                      variant="outline"
                      data-testid="mobile-nav-admin-inventory"
                      className="w-full h-10 text-xs bg-transparent border-[#D8A34B]/45 text-[#D8A34B]"
                    >
                      Inventory
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    data-testid="mobile-nav-admin-logout"
                    className="w-full h-10 text-xs text-[#B8B7B2]"
                    onClick={handleLogout}
                  >
                    Log Out
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
